<template>
  <form @submit.prevent="handleSubmit" @keydown.enter.prevent>

    <div class="header">
      <h2 class="mt-4">
        <span class="multiline-text">{{ localData?.customer_nm }}</span> <!-- 顧客名表示 -->
        <input
          v-model="localData.m_customer_id"
          type="hidden"
        />
      </h2>

      <!-- 顧客名(請求先) 御中 -->
      <div class="schedule-customer-rep d-flex align-items-center mb-3">
        <input v-model="localData.destination_address" id="destination_address" type="text" placeholder="送付先宛名" class="form-control w-half">
      </div>
    </div>

    <h3 class="text-center">{{ localData.work_year }}年{{ localData.work_month }}月度 作業予定表</h3>

    <!-- 業務内容のテーブル -->
    <table class="table table-bordered monthly-detail-data">
      <thead>
        <tr>
          <th class="month-data-header align-middle">現場名</th>
          <th class="month-data-header align-middle">作業内容</th>
          <th class="month-data-header align-middle">日<br><span class="text-danger">請求書の日付に反映されます</span></th>
          <th class="month-data-header align-middle">曜</th>
          <th class="month-data-header align-middle">作業開始時間</th>
          <th class="month-data-header align-middle">作業終了時間</th>
          <th class="month-data-header align-middle">備考</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(schedule, index) in localData.t_annual_work_monthly_calendar_details"
          :key="index"
        >
          <!-- 建物列：最初の1回だけ表示して rowspan -->
          <!-- ▼ 建物列：分岐ロジックを整理 -->
          <template
            v-if="groupedBuildings[schedule.m_building_id]?.indexes[0] === index"
          >
            <!-- 初期表示の行、かつ最初の行のみ rowspan -->
            <td
              class="text-start"
              :rowspan="groupedBuildings[schedule.m_building_id].count"
            >
              <input type="hidden" v-model="schedule.m_building_id" />
              {{ schedule.building_nm || '' }}
            </td>
          </template>
          <!-- <td v-if="index == 0" :rowspan="localData.t_annual_work_monthly_calendar_details.length">{{ building.building_nm }}</td> -->
          <td>{{ schedule.work_content }}</td>
          
          <!-- 日付入力フィールド -->
          <td>
            <input
              v-model="schedule.work_plan_dt"
              type="date"
              class="form-control"
              :min="nextMonthStart"
              :max="nextMonthEnd"
            />
          </td>

          <!-- 曜日表示フィールド -->
          <td>
            <!-- ✅ 表示専用のテキスト -->
            <span>{{ schedule.weekday }}</span>
            
            <!-- ✅ フォーム送信や保持用の hidden input -->
            <input type="hidden" v-model="schedule.weekday">
          </td>
          <!-- 作業開始時間 -->
          <td>
            <Timepicker
              v-model="schedule.start_work_plan_time"
              :minute-interval="10"
              format="HH:mm"
              manual-input
              hide-clear-button
            />
          </td>

          <!-- 作業終了時間 -->
          <td>
            <Timepicker
              v-model="schedule.end_work_plan_time"
              :minute-interval="10"
              format="HH:mm"
              manual-input
              hide-clear-button
            />
          </td>
          <!-- 備考フィールド -->
          <td>
            <input type="text" v-model="schedule.remarks" class="form-control">
          </td>
        </tr>
      </tbody>
    </table>

    <!-- 特記事項 -->
    <div class="special-notes">
      <table class="special-notes-table">
        <thead>
          <tr>
            <th class="text-start">特記事項</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="note-row">
              <textarea v-model="localData.special_notes" rows="3" class="form-control no-resize"></textarea>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- ボタンエリア -->
    <div class="btn-area">
      <a class="btn btn-secondary" @click="goBack">戻る</a>
      <button type="submit" class="btn btn-primary mx-1">予定表登録</button>
    </div>

  </form>
</template>

<script setup>
import { reactive, computed, defineProps, defineEmits, onMounted } from 'vue';
import Timepicker from "vue3-timepicker";
import "vue3-timepicker/dist/VueTimepicker.css";

const props = defineProps({
  year: Object,
  month: Object,
  customerScheduleData: Object
});

const emit = defineEmits(['update-start-time', 'update-end-time', 'go-back', 'submit']);

const year = reactive(props.year);
const month = reactive(props.month);
const localData = reactive(props.customerScheduleData);

const groupedBuildings = computed(() => {
  const grouped = {};
  localData.t_annual_work_monthly_calendar_details.forEach((calendarDetail, index) => {
    if (calendarDetail.work_type === 2) return;
    const id = calendarDetail.m_building_id;
    if (!grouped[id]) {
      grouped[id] = { count: 0, indexes: [] };
    }
    grouped[id].count++;
    grouped[id].indexes.push(index);
  });
  return grouped;
});

// ローカル日付フォーマット関数
const formatDate = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

// 翌月の範囲
const nextMonthStart = computed(() => {
  return formatDate(new Date(year, month - 1, 1));
});

const nextMonthEnd = computed(() => {
  return formatDate(new Date(year, month, 0));
});

// ✅ 初期化時に必ず翌月範囲内に収める
onMounted(() => {
  localData.t_annual_work_monthly_calendar_details.forEach((schedule) => {
    if (
      !schedule.work_plan_dt || 
      schedule.work_plan_dt < nextMonthStart.value || 
      schedule.work_plan_dt > nextMonthEnd.value
    ) {
      schedule.work_plan_dt = nextMonthStart.value; // 翌月1日をセット
    }
  });
});
    
const handleSubmit = () => {
  emit('submit', localData);
};

const goBack = () => {
  emit('go-back');
};
</script>

<style scoped>
.form-container {
  padding: 20px;
  width: 80%;
  margin: auto;
  border: 1px solid #ddd;
  border-radius: 5px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* ヘッダ */
.header h2 {
  font-weight: bold;
}

.header p {
  text-align: center;
  margin-bottom: 20px;
}

.schedule-customer-rep .relative{
  min-width: 600px;
  max-width: 600px;
}

.text-center {
  text-align: center;
}

/* テーブルのスタイル */
table {
  border-collapse: collapse;
  width: 100%;
}

th, td {
  padding: 8px;
  border: 1px solid #000;
  text-align: center;
}

.small-input {
  width: 50px;
}

.month-data-header {
  background-color: #f0f8ff;
  font-weight: bold;
}

/* 特記事項のスタイル */
.special-notes-table {
  width: 100%;
  margin-top: 20px;
  border-collapse: collapse;
}

.special-notes-table th, .special-notes-table td {
  border: 1px solid #000;
}

.no-resize {
  resize: none;
}

/* ボタンエリア */
.btn-area {
  text-align: right;
  margin-top: 20px;
}
</style>
